import { h, setChildren } from "../core/dom.js";
import { api } from "../api.js";
import { profile, loadProfile, getBodyMap, setBodyMap } from "../store.js";
import { navigate } from "../core/router.js";
import { icon } from "../lib/icons.js";
import { toast } from "../components/ui.js";
import { toDisplayWeight, toStorageKg, weightLabel, toDisplayLength, toStorageCm, lengthUnit } from "../lib/format.js";
import { notificationsSupported, permissionState, requestPermission, restAlertsEnabled, setRestAlerts } from "../lib/notify.js";
import { pushSupported, enablePush } from "../lib/push.js";

// First-run setup + guided tour. Shown to brand-new accounts (profile().onboarded === 0)
// and re-triggerable from Settings → "Replay setup & tour".
//
// Flow: welcome → quick choices (units / sex / goal) → about you (weight + height) →
// measurements (how-to + optional waist) → notifications → privacy → a short tour of what
// the app is for → done. Everything is optional and skippable; finishing OR skipping flips
// users.onboarded so it never auto-appears again. Toggle-style settings (notifications,
// privacy) persist immediately as they're flipped; the text choices save on finish/skip.
export default function Onboarding() {
  const root = h("div", { class: "screen onboard" });
  let step = 0;
  let saving = false;
  // Replaying from Settings (already onboarded) tweaks a little copy.
  const replay = !!profile()?.onboarded;
  const choices = {
    weight_unit: profile()?.weight_unit || "kg",
    weekly_goal: profile()?.weekly_goal || 3,
    body_map: getBodyMap(),
    weight: profile()?.bodyweight_kg != null ? String(toDisplayWeight(profile().bodyweight_kg)) : "",
    height: profile()?.height_cm != null ? String(toDisplayLength(profile().height_cm)) : "",
    birth_year: profile()?.birth_year != null ? String(profile().birth_year) : "",
    training_goal: profile()?.training_goal || null,
    training_goal_secondary: profile()?.training_goal_secondary || null,
    waist: "", // optional first measurement — left blank so a replay can't double-log
  };

  // Training focuses, shared by the onboarding step and Settings card. `general` = a balanced base.
  const GOAL_OPTS = [
    { value: "hypertrophy", label: "Muscle growth", sub: "Build size" },
    { value: "strength", label: "Strength", sub: "Lift heavier" },
    { value: "endurance", label: "Endurance", sub: "Cardio & stamina" },
    { value: "general", label: "General fitness", sub: "A bit of everything" },
  ];

  // Each step: { kind, title, render() }.
  const STEPS = [
    { kind: "intro", title: replay ? "Welcome back" : "Welcome to Kashtu", render: welcomeStep },
    { kind: "setup", title: "Quick setup", render: unitsStep },
    { kind: "setup", title: "Quick setup", render: sexStep },
    { kind: "setup", title: "Quick setup", render: goalStep },
    { kind: "setup", title: "Training focus", render: focusStep },
    { kind: "data", title: "About you", render: aboutYouStep },
    { kind: "data", title: "Measurements", render: measureStep },
    { kind: "prefs", title: "Notifications", render: notifyStep },
    { kind: "prefs", title: "Privacy", render: privacyStep },
    { kind: "tour", title: "How it works", render: logTour },
    { kind: "tour", title: "How it works", render: planTour },
    { kind: "tour", title: "How it works", render: trackTour },
    { kind: "done", title: "You're all set", render: doneStep },
  ];

  function render() {
    const cur = STEPS[step];
    const last = step === STEPS.length - 1;
    setChildren(root,
      h("div", { class: "onboard-top" },
        h("div", { class: "auth-logo", style: { margin: 0 } },
          h("div", { class: "mark" }, icon("bow", { size: 28 })),
          h("h1", { style: { fontSize: "1.4rem" } }, cur.title)),
        h("button", { class: "btn-ghost-link", onClick: skip }, replay ? "Close" : "Skip")
      ),
      h("div", { class: "onboard-dots" },
        ...STEPS.map((_, i) => h("span", { class: ["onboard-dot", i === step && "on", i < step && "done"] }))),
      h("div", { class: "card onboard-card" }, cur.render()),
      h("div", { class: "btn-row mt" },
        step > 0 ? h("button", { class: "btn btn-ghost", onClick: () => { step--; render(); } }, "Back") : null,
        h("button", { class: "btn btn-primary grow", onClick: next },
          step === 0 ? "Get started" : last ? (replay ? "Done" : "Start training") : "Next")
      )
    );
  }

  // ── Intro ────────────────────────────────────────────────────────────────────
  function welcomeStep() {
    return h("div", null,
      h("div", { class: "onboard-hero" }, icon("bow", { size: 30 })),
      h("div", { class: "onboard-lead" }, "Your training, tracked."),
      h("p", { class: "dim", style: { marginTop: "4px" } },
        "Kashtu helps you plan workouts, log every set as you lift, and watch yourself get stronger over time."),
      featureList([
        ["dumbbell", "Log it live", "Tick off each set at the gym — weights, reps, cardio, stretching."],
        ["calendar", "Plan ahead", "Build templates and multi-week programs, or follow one you found."],
        ["chart", "See progress", "Streaks, personal records, charts and body tracking in one place."],
      ]),
      h("p", { class: "dim onboard-fineprint" }, "Takes about a minute, and every step is optional. You can redo this later from Settings.")
    );
  }

  // ── Setup steps ──────────────────────────────────────────────────────────────
  function unitsStep() {
    return stepWrap("How do you track weight?", "Also sets your distance and measurement units.",
      pickRow([
        { value: "kg", label: "Kilograms", sub: "kg · km · cm" },
        { value: "lbs", label: "Pounds", sub: "lbs · mi · in" },
      ], choices.weight_unit, async (v) => {
        choices.weight_unit = v;
        render();
        // Persist immediately so later steps convert weight/height/waist in the right unit.
        try { profile(await api.updateMe({ weight_unit: v })); } catch { /* saved again on finish */ }
      })
    );
  }

  function sexStep() {
    return stepWrap("What's your sex?", "Sets your body diagram and tailors the proportion guides. Change it any time.",
      pickRow([
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
      ], choices.body_map, (v) => { choices.body_map = v; render(); })
    );
  }

  function goalStep() {
    const dec = () => { choices.weekly_goal = Math.max(1, choices.weekly_goal - 1); render(); };
    const inc = () => { choices.weekly_goal = Math.min(7, choices.weekly_goal + 1); render(); };
    return stepWrap("Weekly workout goal", "Drives your streak and the dashboard ring.",
      h("div", { class: "onboard-stepper" },
        h("button", { class: "btn btn-ghost", onClick: dec, "aria-label": "Fewer" }, "−"),
        h("div", { class: "onboard-goal" },
          h("div", { class: "num" }, String(choices.weekly_goal)),
          h("div", { class: "dim" }, `workout${choices.weekly_goal === 1 ? "" : "s"} / week`)),
        h("button", { class: "btn btn-ghost", onClick: inc, "aria-label": "More" }, "+")
      )
    );
  }

  // Training goal: a primary focus (tap again to clear) + an optional secondary. Drives the
  // goal-specific coaching tips (rep ranges, rest, straight-sets-vs-circuit, which to train first).
  function focusStep() {
    const setPrimary = (v) => {
      choices.training_goal = choices.training_goal === v ? null : v;
      if (!choices.training_goal || choices.training_goal_secondary === choices.training_goal)
        choices.training_goal_secondary = null;
      render();
    };
    const setSecondary = (v) => {
      choices.training_goal_secondary = choices.training_goal_secondary === v ? null : v;
      render();
    };
    return stepWrap("What's your main training goal?", "Tailors your coaching — rep ranges, rest, and how to run each workout. Optional, and you can change it anytime.",
      h("div", null,
        pickRow(GOAL_OPTS, choices.training_goal, setPrimary),
        choices.training_goal
          ? h("div", { class: "mt" },
              h("p", { class: "dim", style: { fontSize: ".82rem", fontWeight: 600, marginBottom: "6px" } }, "Secondary focus (optional)"),
              h("div", { class: "onboard-picks" },
                ...GOAL_OPTS.filter((o) => o.value !== choices.training_goal).map((o) =>
                  h("button", { class: ["onboard-pick", o.value === choices.training_goal_secondary && "on"], onClick: () => setSecondary(o.value) },
                    h("div", { style: { fontWeight: 700 } }, o.label)))))
          : null
      )
    );
  }

  // ── Data steps (weight / height / first measurement) ─────────────────────────
  function aboutYouStep() {
    const wInput = numInput(choices.weight, `Weight in ${weightLabel()}`, (v) => { choices.weight = v; }, weightLabel() === "kg" ? "0.05" : "1");
    const hInput = numInput(choices.height, `Height in ${lengthUnit()}`, (v) => { choices.height = v; });
    // Birth year (whole number, not decimal): powers age-aware cardio heart-rate zones.
    const yInput = h("input", {
      class: "input", type: "number", inputmode: "numeric", step: "1", min: "1900",
      value: choices.birth_year, placeholder: "e.g. 1990",
      onInput: (e) => { choices.birth_year = e.target.value; },
    });
    return h("div", null,
      h("div", { class: "onboard-hero" }, icon("weight", { size: 28 })),
      h("div", { class: "onboard-lead" }, "About you"),
      h("p", { class: "dim", style: { marginTop: "4px" } },
        "Optional — your weight powers progress charts and strength standards; height adds a BMI readout; birth year tunes cardio heart-rate zones."),
      h("div", { class: "field mt" }, h("label", null, `Bodyweight (${weightLabel()})`), wInput),
      h("div", { class: "field" }, h("label", null, `Height (${lengthUnit()})`), hInput),
      h("div", { class: "field" }, h("label", null, "Birth year"), yInput),
      h("p", { class: "dim onboard-fineprint" }, "Add or update these any time from the Weight screen and Settings.")
    );
  }

  function measureStep() {
    const input = numInput(choices.waist, `Waist in ${lengthUnit()}`, (v) => { choices.waist = v; });
    return h("div", null,
      h("div", { class: "onboard-hero" }, icon("ruler", { size: 28 })),
      h("div", { class: "onboard-lead" }, "Track your shape"),
      h("p", { class: "dim", style: { marginTop: "4px" } },
        "Your waist is often a truer progress signal than the scale. How to measure it consistently:"),
      h("ul", { class: "onboard-tips" },
        h("li", null, "Use a soft tape measure against bare skin."),
        h("li", null, "Wrap it around your waist at the belly button, after breathing out — don't suck in."),
        h("li", null, "Keep it snug and level all the way round, not squeezing."),
        h("li", null, "Measure at the same time each time — first thing in the morning works best.")
      ),
      h("div", { class: "field mt" }, h("label", null, `Waist now (${lengthUnit()}) — optional`), input),
      h("p", { class: "dim onboard-fineprint" }, "Log arms, chest, hips and more any time on the Measurements screen.")
    );
  }

  // ── Preference steps (persist immediately) ───────────────────────────────────
  function notifyStep() {
    if (!notificationsSupported()) {
      return h("div", null,
        h("div", { class: "onboard-hero" }, icon("info", { size: 28 })),
        h("div", { class: "onboard-lead" }, "Notifications"),
        h("p", { class: "dim", style: { marginTop: "4px" } },
          "This browser can't show notifications. On iPhone, add Kashtu to your Home Screen first, then enable them in Settings.")
      );
    }
    const p = profile() || {};
    const restOn = restAlertsEnabled() && permissionState() === "granted";
    return h("div", null,
      h("div", { class: "onboard-hero" }, icon("timer", { size: 28 })),
      h("div", { class: "onboard-lead" }, "Stay on track"),
      h("p", { class: "dim", style: { marginTop: "4px" } }, "All optional, and tunable later in Settings."),
      toggleRow("Rest timer alerts", "Buzz when your rest between sets is up — even with the screen off.", restOn,
        async (want) => {
          if (want && permissionState() !== "granted") {
            const res = await requestPermission();
            if (res !== "granted") { toast("Notifications were not allowed", "warn"); render(); return; }
          }
          setRestAlerts(want);
          render();
        }),
      pushSupported()
        ? toggleRow("Workout reminders", "A daily nudge if you haven't trained and are short of your weekly goal.", !!p.reminders_enabled,
            async (want) => {
              try {
                if (want) await enablePush();
                profile(await api.updateMe({ reminders_enabled: want, reminder_hour: p.reminder_hour ?? 18 }));
              } catch (e) { toast(e.message || "Couldn't enable notifications", "error"); }
              render();
            })
        : null,
      pushSupported()
        ? toggleRow("Weight reminders", "A daily reminder to log your bodyweight, skipped once you've weighed in.", !!p.weight_reminder_enabled,
            async (want) => {
              try {
                if (want) await enablePush();
                profile(await api.updateMe({ weight_reminder_enabled: want, weight_reminder_hour: p.weight_reminder_hour ?? 8 }));
              } catch (e) { toast(e.message || "Couldn't enable notifications", "error"); }
              render();
            })
        : null
    );
  }

  function privacyStep() {
    const p = profile() || {};
    const priv = p.is_private == null ? true : !!p.is_private;
    const share = p.share_activity == null ? true : !!p.share_activity;
    return h("div", null,
      h("div", { class: "onboard-hero" }, icon("lock", { size: 28 })),
      h("div", { class: "onboard-lead" }, "Your privacy"),
      h("p", { class: "dim", style: { marginTop: "4px" } }, "You decide what others can see. Change these any time in Settings."),
      toggleRow("Private account", "Approve who can follow you. Turn off to let anyone follow without approval.", priv,
        async (want) => {
          try { profile(await api.updateMe({ is_private: want })); } catch (e) { toast(e.message, "error"); }
          render();
        }),
      toggleRow("Share activity with followers", "Let accepted followers see your workouts, training status and stats.", share,
        async (want) => {
          try { profile(await api.updateMe({ share_activity: want })); } catch (e) { toast(e.message, "error"); }
          render();
        })
    );
  }

  // ── Tour steps ───────────────────────────────────────────────────────────────
  function logTour() {
    return tourWrap("dumbbell", "Log every set, live",
      "Start a workout and tick sets off as you go — the app remembers everything.",
      [
        ["check", "Set-by-set", "Weight × reps, bodyweight reps, or duration & distance for cardio and mobility."],
        ["timer", "Rest timer", "Auto-starts between sets and buzzes your phone when it's time for the next one."],
        ["history", "Never lose a set", "Resume right where you left off — even after a reload, and edits work offline."],
      ]);
  }

  function planTour() {
    return tourWrap("calendar", "Plan your training",
      "Decide what to train before you walk in — or just start fresh.",
      [
        ["list", "Templates", "Save your go-to workouts, or let the Wizard build one from your goals."],
        ["calendar", "Programs", "Follow a multi-week plan day by day, with the next session always queued up."],
        ["search", "Exercise library", "874 exercises with instructions and muscle maps — add your own too."],
      ]);
  }

  function trackTour() {
    return tourWrap("chart", "Watch your progress",
      "Every set you log feeds the numbers that show you're improving.",
      [
        ["trophy", "Records & streaks", "Personal bests, strength standards and a weekly-goal streak on your dashboard."],
        ["chart", "Charts & volume", "1RM trends and weekly sets per muscle so you train each one enough — not too much."],
        ["weight", "Track your body", "Log bodyweight, measurements and progress photos to see the whole picture."],
      ]);
  }

  // ── Done ─────────────────────────────────────────────────────────────────────
  function doneStep() {
    return h("div", null,
      h("div", { class: "onboard-hero" }, icon("check", { size: 30 })),
      h("div", { class: "onboard-lead" }, replay ? "That's the tour." : "You're ready to train."),
      h("p", { class: "dim", style: { marginTop: "4px" } },
        "Hit the + in the bottom bar any time to start a workout. Here are a couple of ways to begin:"),
      h("div", { class: "onboard-actions mt" },
        actionRow("plus", "Start a workout", "Jump straight in and log on the fly", () => done("/start")),
        actionRow("list", "Build a template", "Plan a reusable workout first", () => done("/workouts/new")),
        actionRow("search", "Browse exercises", "See what's in the library", () => done("/library"))
      )
    );
  }

  // ── Building blocks ──────────────────────────────────────────────────────────
  function stepWrap(title, sub, control) {
    return h("div", null,
      h("div", { style: { fontWeight: 800, fontSize: "1.1rem" } }, title),
      h("p", { class: "dim", style: { marginTop: "2px" } }, sub),
      h("div", { class: "mt" }, control)
    );
  }

  function tourWrap(heroIcon, title, sub, items) {
    return h("div", null,
      h("div", { class: "onboard-hero" }, icon(heroIcon, { size: 28 })),
      h("div", { class: "onboard-lead" }, title),
      h("p", { class: "dim", style: { marginTop: "4px" } }, sub),
      featureList(items)
    );
  }

  function featureList(items) {
    return h("div", { class: "onboard-features mt" },
      ...items.map(([ic, title, desc]) =>
        h("div", { class: "onboard-feature" },
          h("div", { class: "fi-ic" }, icon(ic, { size: 20 })),
          h("div", null,
            h("div", { class: "fi-t" }, title),
            h("div", { class: "fi-d dim" }, desc))
        ))
    );
  }

  // A labelled toggle row. The text is left-aligned; the switch sits on the right. Used by the
  // notifications + privacy steps, which persist each flip immediately.
  function toggleRow(title, desc, on, onChange) {
    return h("div", { class: "onboard-row" },
      h("div", { class: "grow" },
        h("div", { class: "fi-t" }, title),
        h("div", { class: "fi-d dim" }, desc)),
      h("button", { class: ["switch", on && "on"], role: "switch", "aria-checked": on ? "true" : "false", onClick: () => onChange(!on) },
        h("span", { class: "switch-knob" }))
    );
  }

  // Uncontrolled number input that mirrors its value into `choices` on input WITHOUT a
  // re-render (so focus/caret aren't lost mid-typing). The value is re-read on finish.
  function numInput(value, placeholder, onValue, step = "0.1") {
    return h("input", {
      class: "input", type: "number", inputmode: "decimal", step,
      value, placeholder, onInput: (e) => onValue(e.target.value),
    });
  }

  function actionRow(ic, title, desc, onClick) {
    return h("button", { class: "onboard-action", onClick },
      h("div", { class: "fi-ic" }, icon(ic, { size: 20 })),
      h("div", { class: "grow" },
        h("div", { class: "fi-t" }, title),
        h("div", { class: "fi-d dim" }, desc)),
      icon("forward", { size: 18 })
    );
  }

  function pickRow(options, current, onPick) {
    return h("div", { class: "onboard-picks" },
      ...options.map((o) =>
        h("button", { class: ["onboard-pick", o.value === current && "on"], onClick: () => onPick(o.value) },
          h("div", { style: { fontWeight: 700 } }, o.label),
          o.sub ? h("div", { class: "dim", style: { fontSize: ".8rem" } }, o.sub) : null
        ))
    );
  }

  // ── Persist ────────────────────────────────────────────────────────────────
  async function next() {
    if (step < STEPS.length - 1) { step++; render(); return; }
    await finish();
  }

  // Save the text choices (units / goal / sex / height) + flip onboarded. Shared by finish
  // and skip so a mid-flow skip still keeps whatever was entered. Toggle settings already
  // persisted themselves as they were flipped.
  async function persistCore() {
    const update = {
      weight_unit: choices.weight_unit, weekly_goal: choices.weekly_goal, gender: choices.body_map, onboarded: 1,
      training_goal: choices.training_goal || null,
      training_goal_secondary: choices.training_goal ? (choices.training_goal_secondary || null) : null,
    };
    const heightCm = toStorageCm(choices.height);
    if (heightCm != null && heightCm > 0) update.height_cm = Math.round(heightCm * 10) / 10;
    // Only send a sane birth year (age 10–100) so a typo never 400s the whole onboarding save.
    const by = parseInt(choices.birth_year, 10);
    const curYear = new Date().getFullYear();
    if (Number.isInteger(by) && by >= curYear - 100 && by <= curYear - 10) update.birth_year = by;
    profile(await api.updateMe(update));
    setBodyMap(choices.body_map);
  }

  async function finish(dest = "/") {
    if (saving) return;
    saving = true;
    try {
      await persistCore();
      // Log a weight entry only if it's new or differs from the current value (avoids a
      // duplicate on replay where the field is prefilled).
      const wKg = toStorageKg(choices.weight);
      const curKg = profile()?.bodyweight_kg;
      if (wKg != null && wKg > 0 && (curKg == null || Math.abs(wKg - curKg) > 0.05))
        await api.logBodyweight({ weight_kg: wKg, source: "manual" }).catch(() => {});
      const waistCm = toStorageCm(choices.waist);
      if (waistCm != null && waistCm > 0)
        await api.logMeasurement({ site: "waist", value_cm: waistCm }).catch(() => {});
      await loadProfile();
      if (!replay) toast("You're all set 💪", "success");
      navigate(dest, { replace: true });
    } catch (e) {
      saving = false;
      toast(e.message || "Couldn't save — try again", "error");
    }
  }

  // A done-screen CTA: save everything, then deep-link to the chosen starting point.
  function done(dest) {
    finish(dest);
  }

  async function skip() {
    if (saving) return;
    saving = true;
    try { await persistCore(); } catch { /* non-fatal — still leave the flow */ }
    navigate("/", { replace: true });
  }

  render();
  return root;
}
